// @flow

import { isEmpty } from 'lodash';
import { PhoneNumberUtil } from 'google-libphonenumber';

import {
  DISPLAY_NAME_LENGTH,
  EMAIL_REGEXP,
  FIRST_NAME_LENGTH,
  LANGUAGE_FRENCH,
  RRCODE_REGEXP,
  SELECT_EN,
  SELECT_FR,
} from '../constants';

// validate Number input and format as (xxx) xxx-xxxx
export const normalizeInput = (value: string, previousValue: string): string => {
  if (!value) return value;
  const currentValue = value.replace(/[^\d]/g, '');
  const cvLength = currentValue.length;

  if (!previousValue || value.length > previousValue.length) {
    if (cvLength < 4) return currentValue;
    if (cvLength < 7) return `(${currentValue.slice(0, 3)}) ${currentValue.slice(3)}`;
    return `(${currentValue.slice(0, 3)}) ${currentValue.slice(3, 6)}-${currentValue.slice(6)}`;
  }
  return value;
};

// trunction phonenumber to countryCode and phonenumber
export const truncPhoneNumber = (value: string): Object => {
  if (!value) return null;

  const pureNum = value && value.replace(/[^0-9]/gi, '');
  const phoneNum = 'phoneNum';
  const countryCode = 'countryCode';
  const formattedPhone = {};
  if (pureNum) {
    const cvLength = pureNum && pureNum.length;

    if (cvLength < 10 || cvLength === 10) {
      formattedPhone[phoneNum] = pureNum;
      formattedPhone[countryCode] = 1;
    } else if (cvLength > 13) {
      formattedPhone[phoneNum] = pureNum.slice(-10);
      formattedPhone[countryCode] = 1;
    } else {
      formattedPhone[phoneNum] = pureNum.slice(-10);
      formattedPhone[countryCode] = pureNum.slice(0, cvLength - 10);
    }
  }

  return formattedPhone;
};

// initialized phone number as (xxx) xxx-xxxx
export const initialNormalizeValue = (value: string): string => {
  if (!value) return '';
  const pureNum = value.replace(/[^\d]/g, '');
  const cvLength = pureNum.length;
  if (cvLength < 4) return `(${pureNum.slice(0, 3)})`;
  if (cvLength < 7) return `(${pureNum.slice(0, 3)}) ${pureNum.slice(3)}`;
  if (cvLength <= 10)
    return `(${pureNum.slice(0, 3)}) ${pureNum.slice(3, 6)}-${pureNum.slice(6, 10)}`;
  // more than 10 length
  return `(${pureNum.slice(0, 3)}) ${pureNum.slice(3, 6)}-${pureNum.slice(6, 10)}${pureNum.slice(
    10
  )}`;
};

// format Number to selectItems
export const selectPhoneNumberList = (mobileList: Array<any>): Array<any> => {
  const newList =
    mobileList &&
    mobileList.map((item, index) => {
      const newPhoneNum = truncPhoneNumber(item.phoneNum);
      const newLabel = initialNormalizeValue(newPhoneNum.phoneNum);
      const { countryCode } = newPhoneNum;

      return {
        id: index,
        label: newLabel,
        value: index,
        tag: item.phoneType,
        countryCode,
      };
    });
  return newList;
};

// format Number to selectItems
export const selectPhoneCountryList = (
  mobileList: Array<any>,
  countryCodes,
  selectedId
): Array<any> => {
  if (!mobileList || typeof selectedId !== 'number') return [];
  const item = mobileList[selectedId];
  if (!item) return [];
  const phoneData = truncPhoneNumber(item.phoneNum);
  if (ifValidPhoneCountry(phoneData.countryCode, countryCodes)) {
    return phoneData.countryCode;
  }
  return '';
};

// format Number to selectItems
export const selectMaileList = (emailList: Array<any>): Array<any> =>
  emailList &&
  emailList.map((item, index) => ({
    id: index,
    label: item,
    value: index,
  }));

// format address Object to string
const formatAddress = (addressStr: string) => (addressStr ? `${addressStr} ` : '');

export const generateAddress = (address: Object): string =>
  address &&
  `${formatAddress(address.addrLine1)}${formatAddress(address.addrLine2)}${formatAddress(
    address.addrLine3
  )}${formatAddress(address.addrLine4)}${formatAddress(address.addrLine5)}${formatAddress(
    address.addrLine6
  )}${formatAddress(address.addrLine7)}${formatAddress(address.addrLine8)}${formatAddress(
    address.city
  )}${formatAddress(address.prov)}${formatAddress(address.pcode)}${formatAddress(address.country)}`;

// restore formatted Number  (xxx) xxx-xxxx to normal as xxxxxxxxxx
export const restoreFormat = (value: string): string => {
  if (!value) return value;
  const pureNum = value && value.toString().replace(/[^0-9]/gi, '');
  return pureNum;
};

// combine the first letter of firstName and lastName to circleTitle
export const generateCircleTitle = (firstName: string, lastName: string): string => {
  let firstLetter = '';
  let lastLetter = '';
  if (firstName) {
    firstLetter = firstName.substr(0, 1).toLocaleUpperCase();
  }
  if (lastName) {
    lastLetter = lastName.substr(0, 1).toLocaleUpperCase();
  }
  return firstLetter + lastLetter;
};

// combine the first letter of firstName and lastName to circleTitle
export const formatUserStatus = (userStatus: string, type: string): string => {
  const userFormatList = [
    {
      name: 'REGISTERED',
      value: 'com_active',
      timeInfo: 'com_last_login',
    },
    {
      name: 'INVITED',
      value: 'com_invited',
      timeInfo: 'com_email_sent_on',
    },
    {
      name: 'TERMINATED',
      value: 'com_terminated',
      timeInfo: 'com_terminated_on',
    },
    { name: 'INCOMPLETE', value: 'com_incomplete', timeInfo: 'com_last_saved_on' },
    { name: 'EXPIRED', value: 'com_invitation_expired', timeInfo: 'com_email_sent_on' },
    { name: 'INVITE_FAILED', value: 'com_invited_failed', timeInfo: 'com_last_saved_on' },
  ];
  let displayName = '';
  userFormatList.forEach(item => {
    if (item.name === userStatus) {
      displayName = type === '1' ? item.value : item.timeInfo;
    }
  });
  return displayName;
};

// combine the first letter of firstName and lastName to circleTitle
export const getSelectedLength = (accountList: Array<any>): Array<any> => {
  const selectedLength = [];
  accountList.forEach(item => {
    if (item.selected) {
      selectedLength.push(item.id);
    }
  });
  return selectedLength;
};

// validate Number input
export const isNumber = (value: string): boolean => {
  // const rgx = /^\d{1,}$/;
  if (!value) return false;
  const rgx = /^(\s*|\d{1,3})$/;
  const inputValue = rgx.test(value);
  return inputValue;
};

export const initialRestoreFormat = (value: string): string => {
  if (!value) return value;
  const cvLength = value.length;
  if (cvLength < 4) return value;
  if (cvLength < 10) return `${value.slice(1, 4)}${value.slice(6)}`;
  return `${value.slice(1, 4)}${value.slice(6, 9)}${value.slice(10)}`;
};

// restore formatted Number  (xxx) xxx-xxxx to normal as xxxxxxxxxx
export const phoneRestoreFormat = (value: string): string => {
  if (!value) return value;
  const pureNum = value && value.trim().replace(/[^a-zA_Z0-9]/gi, '');
  return pureNum;
};

// validate Number input
export const phoneNumIsNumber = (value: string): boolean => {
  let isOk = false;
  const rgx = /^(\s*|\d{0,16})$/;
  const formattedString = phoneRestoreFormat(value);
  if (rgx.test(value)) {
    isOk = true;
  } else if (rgx.test(formattedString)) {
    isOk = true;
  }
  return isOk;
};

export const validateEmail = email => {
  let flag = false;
  if (isEmpty(email)) {
    flag = false;
  } else {
    flag = EMAIL_REGEXP.test(email);
  }
  return flag;
};

export const validateName = name => {
  let flag = false;
  if (isEmpty(name)) {
    flag = false;
  } else {
    flag = FIRST_NAME_LENGTH.test(name);
  }
  return flag;
};

export const validateDispalyName = name => {
  let flag = false;
  if (isEmpty(name)) {
    flag = false;
  } else {
    flag = DISPLAY_NAME_LENGTH.test(name);
  }
  return flag;
};

export const validateRrcode = rrcode => {
  let flag = false;
  if (isEmpty(rrcode)) {
    flag = false;
  } else {
    flag = RRCODE_REGEXP.test(rrcode);
  }
  return flag;
};

export const unique = matrix => {
  const res = {};

  matrix.map(item => {
    item.sort((a, b) => a - b);
    res[item] = item;
    return res;
  });
  return Object.values(res);
};

// if country code is in codelist
export const ifValidPhoneCountry = (phoneCode, countryCodes) => {
  let ifValid = false;
  ifValid = countryCodes.some(country => country.value === phoneCode.toString());
  return ifValid;
};

// if country code and phone number is valid
export const validatePhoneGoogle = (countryCode, phoneNumber, countryCodes) => {
  // if countryCode contains number or +( no value condition: select option)
  const hasNumberOrPlus = /[0-9]/.test(countryCode);

  const phoneUtil = PhoneNumberUtil.getInstance();
  const restorePC = restoreFormat(countryCode);
  const restorePN = restoreFormat(phoneNumber);

  try {
    if ((!hasNumberOrPlus && restorePN === '') || (!countryCode && !restorePN)) {
      // if countryCode no value condition:(select option) && phoneNumber is null   or null of both
      return true;
    }

    if (!ifValidPhoneCountry(restorePC, countryCodes)) {
      // if countryCode is not in list
      return false;
    }

    // Combine country calling code and phone number
    const fullNumber = `+${restorePC}${restorePN}`;
    // Parse the number using the international format
    const parsedNumber = phoneUtil.parse(fullNumber);
    // Validate the parsed number
    return phoneUtil.isValidNumber(parsedNumber);
  } catch (error) {
    return false;
  }
};

// if primary mobile is valid
export const ifValidMobile = (primaryMobile, countryCodes) => {
  // Only validate if primaryMobile matches /^\d+\s\d+$/
  const validFormat = /^\d+\s\d+$/;
  if (isEmpty(primaryMobile) || primaryMobile === ' ') {
    return true;
  }
  if (!validFormat.test(primaryMobile)) {
    return false;
  }
  const phoneCode = primaryMobile.split(' ')[0];
  const phoneNum = primaryMobile.split(' ')[1];
  let ifValid = true;
  if (validatePhoneGoogle(phoneCode, phoneNum, countryCodes)) {
    ifValid = true;
  } else {
    ifValid = false;
  }

  return ifValid;
};

// when select the country code, we use the +code to display in label instead of the full text
export const costomizedLabel = selectedLabel => {
  const match = selectedLabel.match(/\(([^)]+)\)/);
  const newLabel = match ? match[1] : null;
  return newLabel;
};

// when use user info, get the phone code from dropdown list
export const getPhoneCodeFromUserInfoDropDownList = mobile => {
  const mobileContent = mobile && selectPhoneNumberList(mobile);
  const mobileNo = mobile && mobile.find(item => item.phoneType === 'mobile');
  const initailPhoneCountry =
    mobileNo && !isEmpty(mobileContent) ? mobileContent[0].countryCode : '';
  return initailPhoneCountry;
};

// when use user info, get the phone number from dropdown list
export const getPhoneNumFromUserInfoDropDownList = mobile => {
  const mobileContent = mobile && selectPhoneNumberList(mobile);
  const mobileNo = mobile && mobile.find(item => item.phoneType === 'mobile');
  const initailPhoneNum = mobileNo && !isEmpty(mobileContent) ? mobileContent[0].label : '';
  return initailPhoneNum;
};

// get initial phone country in non use user info.
export const getDisplayPhoneCodeNonUseInfo = (isEdit, primaryMobile, language, countryCodes) => {
  let phoneCountry = '';
  let ifValidCountrCode = true;
  if (isEdit && primaryMobile) {
    phoneCountry = primaryMobile.split(' ')[0];

    const hasNumberOrPlus = /[0-9]/.test(phoneCountry);
    // if countryCode contains number or +( no value condition: select option)
    if (hasNumberOrPlus) {
      // if countryCode is in list
      ifValidCountrCode = ifValidPhoneCountry(phoneCountry.toString(), countryCodes);
    }
  }

  if (isEdit) {
    // if isEdit && phoneCountry && ifValidCountrCode, use phoneCountry
    // if isEdit && (!phoneCountry || !ifValidCountrCode), use default show select

    return phoneCountry && ifValidCountrCode
      ? `+${phoneCountry}`
      : language === LANGUAGE_FRENCH
      ? SELECT_FR
      : SELECT_EN;
  }

  // if !isEdit,  use default show select
  return language === LANGUAGE_FRENCH ? SELECT_FR : SELECT_EN;
};

// get initial phone num in non use user info.
export const getDisplayPhoneNumNonUseInfo = (isEdit, primaryMobile) => {
  let phoneNum = '';
  if (isEdit && primaryMobile) {
    // if isEdit, use primaryMobile
    phoneNum = primaryMobile.split(' ')[1];
  }

  // if !isEdit, use empty
  return isEdit ? phoneNum : '';
};

// get the phone code when use user info
export const getDisplayPhoneCode = (
  mobile,
  isEdit,
  primaryMobile,
  initailChangePhone,
  language,
  countryCodes
) => {
  // get initailPhoneCountry from mobile list
  let initailPhoneCountry = getPhoneCodeFromUserInfoDropDownList(mobile);
  // if primaryMobile no value, use initailPhoneCountry; else use primaryMobile
  initailPhoneCountry =
    primaryMobile.length === 0 || primaryMobile === ' '
      ? initailPhoneCountry
      : primaryMobile.split(' ')[0];

  let ifValidInitailPhoneCountry = true;
  const hasNumberOrPlus = /[0-9]/.test(initailPhoneCountry);
  if (hasNumberOrPlus) {
    ifValidInitailPhoneCountry = countryCodes.some(
      country => country.value === initailPhoneCountry.toString()
    );
  }

  // define editInitailPhoneCountry
  // if primaryMobile no value, use editInitailPhoneCountry; else use primaryMobile
  const editInitailPhoneCountry =
    isEdit && (primaryMobile === null || primaryMobile.length === 0 || primaryMobile === ' ')
      ? ''
      : primaryMobile && primaryMobile.split(' ')[0];
  let ifValidEditPhoneCountry = true;
  const hasNumberOrPlusEdit = /[0-9]/.test(editInitailPhoneCountry);
  if (hasNumberOrPlusEdit) {
    ifValidEditPhoneCountry = countryCodes.some(
      country => country.value === editInitailPhoneCountry.toString()
    );
  }

  if (!initailChangePhone && ifValidEditPhoneCountry) {
    // if initailChangePhone is false(means not just mount by use user info)
    // use editInitailPhoneCountry
    return editInitailPhoneCountry
      ? `+${editInitailPhoneCountry}`
      : language === LANGUAGE_FRENCH
      ? SELECT_FR
      : SELECT_EN;
  }

  // use initailPhoneCountry
  return initailPhoneCountry && ifValidInitailPhoneCountry
    ? `+${initailPhoneCountry}`
    : language === LANGUAGE_FRENCH
    ? SELECT_FR
    : SELECT_EN;
};

// get the phone number when use user info
export const getDisplayPhoneNum = (mobile, isEdit, primaryMobile, initialChangePhone) => {
  // get value from mobile list as initialPhoneNum
  let initialPhoneNum = getPhoneNumFromUserInfoDropDownList(mobile);
  initialPhoneNum =
    primaryMobile.length === 0 || primaryMobile === ' '
      ? initialPhoneNum
      : primaryMobile && initialNormalizeValue(primaryMobile.split(' ')[1]);

  // get value from primaryMobile as editInitialPhoneNum
  const editInitialPhoneNum =
    isEdit && (primaryMobile.length === 0 || primaryMobile === ' ')
      ? ''
      : primaryMobile && initialNormalizeValue(primaryMobile.split(' ')[1]);

  if (!initialChangePhone) {
    // if not just to choose use user info, use editInitialPhoneNum
    return editInitialPhoneNum;
  }
  return initialPhoneNum;
};

// format Number to selectItems
/**
 * format (xxx) xxx-xxxxXXXX
 * @param {string} value
 * @returns {string}
 */
export const formatPhoneNumber = (pureNum: string): string => {
  if (!pureNum) return '';
  const cvLength = pureNum.length;
  if (cvLength < 4) return `(${pureNum.slice(0, 3)})`;
  if (cvLength < 7) return `(${pureNum.slice(0, 3)}) ${pureNum.slice(3)}`;
  if (cvLength <= 10)
    return `(${pureNum.slice(0, 3)}) ${pureNum.slice(3, 6)}-${pureNum.slice(6, 10)}`;
  // more than 10 length
  return `(${pureNum.slice(0, 3)}) ${pureNum.slice(3, 6)}-${pureNum.slice(6, 10)}${pureNum.slice(
    10
  )}`;
};
